import React, { useState } from "react";
import toast from "react-hot-toast";
import { ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import Spinner from "@/components/Spinner";
import { useAppSelector } from "@/redux/features/hooks";
import { Roles, User } from "@/redux/services/user/user.type";
import { actionLabel, domainLabel, summarizePermissions } from "@/lib/permissions";
import {
  useAssignAccessRoleMutation,
  useGetAccessRolesQuery,
  useGetEmployeesQuery,
  useGetSystemPresetsQuery,
  useUnassignAccessRoleMutation,
} from "@/redux/services/permission/accessRole.api";
import {
  AccessRole,
  AccessRoleScope,
} from "@/redux/services/permission/permission.type";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  user: User;
}

const summarize = (permissions: string[]) =>
  summarizePermissions(permissions)
    .map(
      ({ domain, actions }) =>
        `${domainLabel(domain)} · ${actions.map(actionLabel).join(" / ")}`
    )
    .join(", ");

const AssignAccessRolesModal: React.FC<Props> = ({ isOpen, onClose, user }) => {
  const { currentUser, currentRestaurant, currentFranchise } = useAppSelector(
    (s) => s.user
  );
  const isOwner = currentUser?.roles?.includes(Roles.owner);

  const scope: AccessRoleScope | null = isOwner
    ? currentFranchise?.id
      ? { franchiseId: currentFranchise.id }
      : null
    : currentRestaurant?.id
      ? { restaurantId: currentRestaurant.id }
      : null;

  const { data: roles, isLoading: rolesLoading } = useGetAccessRolesQuery(
    scope as AccessRoleScope,
    { skip: !scope }
  );
  const { data: employees, isLoading: employeesLoading } = useGetEmployeesQuery(
    scope as AccessRoleScope,
    { skip: !scope }
  );
  const { data: presets, isLoading: presetsLoading } =
    useGetSystemPresetsQuery();

  const [assignAccessRole] = useAssignAccessRoleMutation();
  const [unassignAccessRole] = useUnassignAccessRoleMutation();
  const [pendingRoleId, setPendingRoleId] = useState<string | null>(null);

  const employee = employees?.find((e) => e.employeeUser?.id === user.id);
  const assignmentByRoleId = new Map(
    employee?.accessRoleAssignments?.map((a) => [a.accessRole.id, a.id]) ?? []
  );

  const isLoading = rolesLoading || employeesLoading || presetsLoading;
  const hasAnyRole = !!roles?.length || !!presets?.length;

  const handleToggle = async (roleId: string) => {
    if (!employee) return;
    setPendingRoleId(roleId);
    try {
      const assignmentId = assignmentByRoleId.get(roleId);
      if (assignmentId) {
        await unassignAccessRole(assignmentId).unwrap();
        toast.success("Rôle retiré.");
      } else {
        await assignAccessRole({
          employeeId: employee.id,
          roleId,
        }).unwrap();
        toast.success("Rôle assigné.");
      }
    } catch (error) {
      const raw = JSON.stringify((error as { data?: unknown })?.data ?? "");
      if (raw.includes("already_assigned")) {
        toast.error("Ce rôle est déjà assigné.");
      } else {
        toast.error("L'opération a échoué.");
      }
    } finally {
      setPendingRoleId(null);
    }
  };

  const renderRole = (role: AccessRole) => (
    <label
      key={role.id}
      className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 hover:bg-muted/40"
    >
      <Checkbox
        checked={assignmentByRoleId.has(role.id)}
        disabled={pendingRoleId === role.id}
        onCheckedChange={() => handleToggle(role.id)}
        className="mt-0.5"
      />
      <div className="min-w-0">
        <p className="text-sm font-medium text-card-foreground">{role.name}</p>
        <p className="text-xs text-muted-foreground">
          {summarize(role.permissions)}
        </p>
      </div>
    </label>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader className="mb-4">
          <DialogTitle>Rôles d'accès — {user.fullName}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : !employee ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Cet employé est introuvable dans ce périmètre.
          </p>
        ) : !hasAnyRole ? (
          <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
            <ShieldCheck size={36} strokeWidth={1.5} />
            <p className="text-sm">
              Aucun rôle d'accès disponible. Créez-en un dans l'onglet « Rôles &
              permissions ».
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {!!roles?.length && (
              <div className="space-y-2">{roles.map(renderRole)}</div>
            )}
            {!!presets?.length && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Modèles système
                </p>
                {presets.map(renderRole)}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AssignAccessRolesModal;
